import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';

interface SortableCardRowProps {
  instance: CardInstance;
  isBottom: boolean;
  onToggleBottom: (id: string) => void;
}

function SortableCardRow({ instance, isBottom, onToggleBottom }: SortableCardRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: instance.instanceId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const artUri = getCardImageUri(instance.card, 'artCrop');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-2 py-1.5 rounded transition ${
        isBottom ? 'bg-blue-950/30 border border-blue-800/40' : 'hover:bg-gray-700/60'
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="text-gray-600 hover:text-gray-300 cursor-grab active:cursor-grabbing px-1 shrink-0"
        aria-label="Drag to reorder"
      >
        ⠿
      </button>

      {artUri ? (
        <img src={artUri} alt={instance.card.name} className="w-8 h-8 object-cover rounded shrink-0 border border-gray-700" />
      ) : (
        <div className="w-8 h-8 bg-gray-800 rounded shrink-0 border border-gray-700" />
      )}

      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-200 truncate">{instance.card.name}</div>
        <div className="text-[10px] text-gray-500 truncate">{instance.card.typeLine}</div>
      </div>

      {/* Send to bottom toggle */}
      <button
        onClick={() => onToggleBottom(instance.instanceId)}
        className={`shrink-0 w-7 h-7 flex items-center justify-center rounded border transition text-sm ${
          isBottom
            ? 'border-blue-500 bg-blue-900/50 text-blue-300'
            : 'border-gray-700 bg-gray-800 text-gray-500 hover:border-blue-500 hover:text-blue-400'
        }`}
        title={isBottom ? 'Move to bottom (toggled)' : 'Send to bottom of library'}
        aria-label={isBottom ? 'Remove from bottom' : 'Send to bottom'}
      >
        ↓
      </button>
    </div>
  );
}

export default function ScryPanel() {
  const scryCards = usePlaytesterStore((s) => s.scryCards);
  const scryBottom = usePlaytesterStore((s) => s.scryBottom);
  const scryReorder = usePlaytesterStore((s) => s.scryReorder);
  const scryToggleBottom = usePlaytesterStore((s) => s.scryToggleBottom);
  const scryConfirm = usePlaytesterStore((s) => s.scryConfirm);
  const setOpenPanel = usePlaytesterStore((s) => s.setOpenPanel);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = scryCards.findIndex((c) => c.instanceId === active.id);
    const newIndex = scryCards.findIndex((c) => c.instanceId === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      scryReorder(oldIndex, newIndex);
    }
  }

  const topCards = scryCards.filter((c) => !scryBottom.has(c.instanceId));
  const bottomCards = scryCards.filter((c) => scryBottom.has(c.instanceId));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-[440px] max-h-[70vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 shrink-0">
          <h2 className="text-sm font-semibold text-white">
            Scry {scryCards.length}
          </h2>
          <button
            onClick={() => setOpenPanel(null)}
            className="text-gray-500 hover:text-white transition text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Instructions */}
        <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-800 shrink-0">
          Drag to reorder. Click ↓ to send a card to the bottom of the library.
        </div>

        {/* Sortable card list */}
        <div className="flex-1 overflow-y-auto py-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={scryCards.map((c) => c.instanceId)}
              strategy={verticalListSortingStrategy}
            >
              {scryCards.map((instance) => (
                <SortableCardRow
                  key={instance.instanceId}
                  instance={instance}
                  isBottom={scryBottom.has(instance.instanceId)}
                  onToggleBottom={scryToggleBottom}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        {/* Summary */}
        {(topCards.length > 0 || bottomCards.length > 0) && (
          <div className="px-4 py-2 border-t border-gray-800 text-xs text-gray-500 shrink-0">
            {topCards.length} → top of library
            {bottomCards.length > 0 && ` · ${bottomCards.length} → bottom`}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 px-4 py-3 border-t border-gray-800 shrink-0">
          <button
            onClick={() => setOpenPanel(null)}
            className="flex-1 py-1.5 text-sm text-gray-400 hover:text-white border border-gray-700 hover:border-gray-600 rounded transition"
          >
            Cancel
          </button>
          <button
            onClick={scryConfirm}
            className="flex-1 py-1.5 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded transition"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
